import { useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { EventType } from '@azure/msal-browser';

import { IdleTimerManager } from './IdleTimerManager';
import { environment } from '../../config/environment';
import { authService } from '../../services/auth.service';
import { AuthenticationManagerProps } from './Auth.types';
import { useAppDispatch, useAppSelector } from '../../store';
import { setLoading, setUser, setAuthenticated, setAuthorized, setBackendVersion } from '../../store/slices/global.slice';

/**
 * Manages Azure AD authentication lifecycle.
 *
 * Handles:
 * - Initial Azure authentication check and login redirect
 * - Backend user validation
 * - MSAL login success callbacks
 * - Idle timeout management via IdleTimerManager
 *
 * Token refresh is handled automatically by MSAL's acquireTokenSilent method.
 *
 */
export const AuthenticationManager = ({ onAuthError, onAccessDenied }: AuthenticationManagerProps) => {
  const { instance } = useMsal();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector(state => state.global);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        dispatch(setLoading(true));

        const isAzureAuthenticated = await authService.checkAuthentication();
        if (!isAzureAuthenticated) {
          instance.loginRedirect({
            scopes: environment.azure.scopes,
            redirectUri: environment.azure.redirectUri
          });
          return;
        }

        dispatch(setAuthenticated(true));

        const authResponse = await authService.validateUserWithBackend();
        if (authResponse.user) {
          dispatch(setUser(authResponse.user));
          dispatch(setAuthorized(true));
          dispatch(setBackendVersion(authResponse.version || null));
        } else {
          onAccessDenied();
        }
      } catch (error: any) {
        onAuthError('Authentication initialization failed', error);
      } finally {
        dispatch(setLoading(false));
      }
    };

    const callbackId = instance.addEventCallback(message => {
      if (message.eventType === EventType.LOGIN_SUCCESS) {
        const account = (message.payload as any)?.account;
        if (account) {
          instance.setActiveAccount(account);
        }
      }
    });

    if (!isAuthenticated && !user) {
      initializeAuth();
    }

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance, dispatch, isAuthenticated, user]);

  return <IdleTimerManager />;
};
